import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { SproutIcon } from "../../../components/common/SproutIcon";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

interface Pattern {
  id: number;
  name: string;
  english_name: string;
  name_it: string;
  name_pt: string;
  assignment_count: number;
}

interface PatternFormValues {
  name: string;
  english_name?: string;
  name_it?: string;
  name_pt?: string;
}

/**
 * Admin page for `Pattern` — the catalogue of rootstocks/patrones used
 * on plots, audit plots, plot snapshots and picked plant material.
 *
 * Supports CRUD with a delete-guard that blocks removal when the
 * pattern is referenced, and a bulk merge action that reassigns every
 * reference from selected sources onto a chosen target before
 * deleting the sources.
 *
 * The legacy "English name" column is exposed as `english_name`; the
 * Italian / Portuguese translations live on `name_it` / `name_pt`.
 */
export const PatternsList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [data, setData] = useState<Pattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Pattern | null>(null);
  const [form] = Form.useForm<PatternFormValues>();
  const [saving, setSaving] = useState(false);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [merging, setMerging] = useState(false);

  usePageTitle(t("patterns.title", "Patrones de parcelas"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<Pattern[]>("/admin/patterns/");
      const list = Array.isArray(r.data)
        ? r.data
        : ((r.data as any).results ?? []);
      setData(list);
    } catch {
      notify.error(t("patterns.load_error", "Error al cargar los patrones"));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ----- CRUD ---------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setEditorOpen(true);
  };

  const openEdit = (row: Pattern) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      english_name: row.english_name ?? "",
      name_it: row.name_it ?? "",
      name_pt: row.name_pt ?? "",
    });
    setEditorOpen(true);
  };

  const submitEditor = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await apiClient.patch(`/admin/patterns/${editing.id}/`, values);
        notify.success(t("patterns.update_success", "Patrón actualizado"));
      } else {
        await apiClient.post("/admin/patterns/", values);
        notify.success(t("patterns.create_success", "Patrón creado"));
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        e?.response?.data?.name?.[0] ??
        t("patterns.save_error", "No se pudo guardar el patrón");
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const deletePattern = async (row: Pattern) => {
    try {
      await apiClient.delete(`/admin/patterns/${row.id}/`);
      notify.success(t("patterns.delete_success", "Patrón eliminado"));
      setSelectedRowKeys((keys) => keys.filter((k) => k !== row.id));
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("patterns.delete_error", "No se pudo eliminar el patrón");
      notify.error(msg);
    }
  };

  // ----- merge --------------------------------------------------------

  const selectedRows = useMemo(
    () => data.filter((r) => selectedRowKeys.includes(r.id)),
    [data, selectedRowKeys],
  );

  const openMerge = () => {
    if (selectedRowKeys.length < 2) {
      notify.warning(
        t(
          "patterns.merge_need_two",
          "Selecciona al menos dos patrones para fusionar.",
        ),
      );
      return;
    }
    const heaviest = [...selectedRows].sort(
      (a, b) => b.assignment_count - a.assignment_count,
    )[0];
    setMergeTargetId(heaviest?.id ?? selectedRowKeys[0]);
    setMergeOpen(true);
  };

  const submitMerge = async () => {
    if (!mergeTargetId) return;
    const sources = selectedRowKeys.filter((k) => k !== mergeTargetId);
    if (sources.length === 0) {
      notify.warning(
        t(
          "patterns.merge_no_sources",
          "Selecciona al menos un patrón distinto del destino.",
        ),
      );
      return;
    }
    setMerging(true);
    try {
      const res = await apiClient.post<{
        moved_plots: number;
        moved_audit_plots: number;
        moved_plot_reals: number;
        moved_picked: number;
      }>("/admin/patterns/merge/", {
        target_id: mergeTargetId,
        source_ids: sources,
      });
      const moved =
        (res.data?.moved_plots ?? 0) +
        (res.data?.moved_audit_plots ?? 0) +
        (res.data?.moved_plot_reals ?? 0) +
        (res.data?.moved_picked ?? 0);
      notify.success(
        t(
          "patterns.merge_success",
          "Fusionados {{n}} patrones · {{moved}} referencias reasignadas",
          { n: sources.length, moved },
        ),
      );
      setMergeOpen(false);
      setSelectedRowKeys([]);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("patterns.merge_error", "No se pudo fusionar");
      notify.error(msg);
    } finally {
      setMerging(false);
    }
  };

  // ----- table --------------------------------------------------------

  const columns = [
    {
      title: t("patterns.name", "Nombre"),
      dataIndex: "name",
      key: "name",
      ellipsis: { showTitle: false } as any,
      render: (v: string) => (
        <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.25}>
          <strong>{v}</strong>
        </Tooltip>
      ),
      sorter: (a: Pattern, b: Pattern) => a.name.localeCompare(b.name),
    },
    {
      title: t("patterns.translations", "Traducciones"),
      key: "translations",
      width: 220,
      render: (_: any, row: Pattern) => {
        const entries: Array<{ code: string; label: string; value: string }> = [
          { code: "EN", label: "English", value: row.english_name },
          { code: "IT", label: "Italiano", value: row.name_it },
          { code: "PT", label: "Português", value: row.name_pt },
        ];
        return (
          <Space size={4} wrap>
            {entries.map((e) =>
              e.value ? (
                <Tooltip key={e.code} title={`${e.label}: ${e.value}`}>
                  <Tag color="blue" style={{ margin: 0 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip
                  key={e.code}
                  title={`${e.label}: ${t("patterns.no_translation", "sin traducir")}`}
                >
                  <Tag color="default" style={{ margin: 0, opacity: 0.55 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ),
            )}
          </Space>
        );
      },
    },
    {
      title: t("patterns.assignment_count", "Parcelas asignadas"),
      dataIndex: "assignment_count",
      key: "assignment_count",
      width: 180,
      align: "center" as const,
      sorter: (a: Pattern, b: Pattern) =>
        a.assignment_count - b.assignment_count,
      render: (n: number) =>
        n > 0 ? <Tag color="blue">{n}</Tag> : <Tag color="default">0</Tag>,
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 160,
      align: "right" as const,
      render: (_: any, row: Pattern) => (
        <Space size="small">
          <Tooltip title={t("common.edit", "Editar")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Popconfirm
            title={t("patterns.delete_confirm", "¿Eliminar este patrón?")}
            description={
              row.assignment_count > 0
                ? t(
                    "patterns.delete_blocked",
                    "No puedes eliminar un patrón asignado. Fusiónalo primero.",
                  )
                : t(
                    "patterns.delete_warning",
                    "Esta acción no se puede deshacer.",
                  )
            }
            onConfirm={() => deletePattern(row)}
            okText={t("common.yes", "Sí")}
            cancelText={t("common.no", "No")}
            okButtonProps={{ danger: true }}
            disabled={row.assignment_count > 0}
          >
            <Tooltip
              title={
                row.assignment_count > 0
                  ? t(
                      "patterns.delete_blocked_tooltip",
                      "Tiene parcelas asignadas — no se puede eliminar",
                    )
                  : t("common.delete", "Eliminar")
              }
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={row.assignment_count > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<SproutIcon />}
        accent="#389E0D"
        title={t("patterns.title", "Patrones de parcelas")}
        subtitle={t(
          "patterns.subtitle",
          "Catálogo de patrones (portainjertos) asignables a parcelas. Fusiona los duplicados para mantener la lista limpia.",
        )}
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
            <Tooltip
              title={
                selectedRowKeys.length < 2
                  ? t(
                      "patterns.merge_hint_tooltip",
                      "Marca las casillas de 2 o más patrones en la tabla para poder fusionarlos",
                    )
                  : ""
              }
            >
              <Button
                icon={<MergeCellsOutlined />}
                onClick={openMerge}
                type={selectedRowKeys.length >= 2 ? "primary" : "default"}
              >
                {t("patterns.merge", "Fusionar")}
                {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("patterns.create", "Nuevo patrón")}
            </Button>
          </Space>
        }
      />

      {selectedRowKeys.length < 2 && (
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 12 }}
          message={t(
            "patterns.merge_banner",
            "Cómo fusionar: marca las casillas (☐) de los patrones duplicados en la tabla y pulsa Fusionar. Podrás elegir cuál mantener y las parcelas del resto se reasignarán automáticamente.",
          )}
        />
      )}

      <Table<Pattern>
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 960 }}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as number[]),
          getCheckboxProps: () => ({ disabled: false }),
          columnWidth: 48,
          fixed: "left",
        }}
      />

      <Modal
        title={
          editing
            ? t("patterns.edit_title", "Editar patrón")
            : t("patterns.create_title", "Nuevo patrón")
        }
        open={editorOpen}
        onCancel={() => (saving ? undefined : setEditorOpen(false))}
        onOk={submitEditor}
        confirmLoading={saving}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("patterns.name_es", "Nombre (Español)")}
            name="name"
            rules={[
              {
                required: true,
                message: t(
                  "patterns.name_required",
                  "El nombre es obligatorio",
                ),
              },
              { max: 100 },
            ]}
            extra={t(
              "patterns.name_es_hint",
              "Idioma principal del catálogo — siempre obligatorio.",
            )}
          >
            <Input
              maxLength={100}
              placeholder={t("patterns.name_placeholder", "Ej: SO4")}
              autoFocus
            />
          </Form.Item>

          <Text
            type="secondary"
            style={{
              fontSize: 12,
              display: "block",
              marginTop: 4,
              marginBottom: 8,
            }}
          >
            {t(
              "patterns.translations_hint",
              "Traducciones opcionales — se usan en los exports de Excel y en la UI según el idioma del usuario.",
            )}
          </Text>

          <Form.Item
            label={t("patterns.name_en", "Nombre (English)")}
            name="english_name"
            rules={[{ max: 100 }]}
          >
            <Input
              maxLength={100}
              placeholder={t("patterns.name_en_placeholder", "e.g. SO4")}
            />
          </Form.Item>

          <Form.Item
            label={t("patterns.name_it", "Nome (Italiano)")}
            name="name_it"
            rules={[{ max: 100 }]}
          >
            <Input
              maxLength={100}
              placeholder={t("patterns.name_it_placeholder", "es. SO4")}
            />
          </Form.Item>

          <Form.Item
            label={t("patterns.name_pt", "Nome (Português)")}
            name="name_pt"
            rules={[{ max: 100 }]}
          >
            <Input
              maxLength={100}
              placeholder={t("patterns.name_pt_placeholder", "ex: SO4")}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("patterns.merge_title", "Fusionar patrones")}
        open={mergeOpen}
        onCancel={() => (merging ? undefined : setMergeOpen(false))}
        onOk={submitMerge}
        confirmLoading={merging}
        okText={t("patterns.merge_apply", "Fusionar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        okButtonProps={{ danger: true }}
        width={520}
      >
        <Text type="secondary">
          {t(
            "patterns.merge_hint",
            "Selecciona el patrón que quieres mantener. Los demás se eliminarán y todas sus parcelas se reasignarán al patrón elegido.",
          )}
        </Text>
        <div style={{ marginTop: 12 }}>
          <Radio.Group
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
          >
            <Space direction="vertical">
              {selectedRows.map((row) => (
                <Radio key={row.id} value={row.id}>
                  <strong>{row.name}</strong>{" "}
                  <Text type="secondary">
                    ({row.assignment_count}{" "}
                    {row.assignment_count === 1
                      ? t("patterns.plot_one", "parcela")
                      : t("patterns.plot_many", "parcelas")})
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    </Card>
  );
};

export default PatternsList;
